import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  NgbTooltipConfig,
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent,
} from "@ng-bootstrap/ng-bootstrap";
import { DateTime, Duration } from "luxon";
import { EMPTY, interval } from "rxjs";
import { forkJoin } from "rxjs";
import { Observable, merge, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { ProjectService } from "src/app/admin/shared/services/project.service";
import { IUser } from "src/app/admin/users/models/IUser";
import { CurrentUserService } from "../../services/current-user.service";
import { ModalRouteService } from "../../services/modal-route.service";
import { TimerService } from "../../services/timer.service";
import { Timer, TimerStatus } from "./timer";

@Component({
  selector: "app-timers",
  templateUrl: "./timers.component.html",
  styleUrls: ["./timers.component.scss"],
  providers: [NgbTooltipConfig],
})
export class TimersComponent implements OnInit, OnDestroy {
  private destroy = new Subject<any>();
  @ViewChild("projectSearch", { static: false }) projectSearch: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();
  allProjects: IProject[] = [];
  recentProjects: IProject[] = [];

  user: IUser;

  TimerStatus = TimerStatus;

  timer$: Observable<Timer> = null;
  timer: Timer = null;
  viewingTimer = false;

  selectedProject: IProject = null;

  constructor(
    private timerService: TimerService,
    private modalService: ModalRouteService,
    tooltipConfig: NgbTooltipConfig,
    projectService: ProjectService,
    currentUserService: CurrentUserService
  ) {
    tooltipConfig.placement = "left";
    tooltipConfig.container = "body";
    tooltipConfig.openDelay = 300;

    this.timer$ = timerService.timer$.pipe(
      takeUntil(this.destroy),
      tap(this.timerUpdates)
    );

    const intervalSeconds = 30; // the time will be accurate but i dont think we need to see this timer perfectly accurate so we dont need to update every second
    const hasActiveTimer$ = this.timer$.pipe(
      map((timer) => (timer ? timer.status === TimerStatus.Active : false))
    );
    hasActiveTimer$
      .pipe(
        switchMap((hasTimers) => {
          if (hasTimers) {
            return interval(intervalSeconds * 1000);
          }
          return EMPTY;
        }),
        takeUntil(this.destroy)
      )
      .subscribe((r) => {
        const now = DateTime.local();
        if (this.timer) {
          const currentMillis =
            DateTime.local().toMillis() -
            this.timer.start.toMillis() +
            this.timer.minutesTimed * 60 * 1000;
          const duration = Duration.fromMillis(currentMillis);
          this.timer.elapsedTimeDisplay = duration.toFormat("hh:mm");
        }
      });

    currentUserService.currentUserData$
      .pipe(
        takeUntil(this.destroy),
        tap((user) => (this.user = user)),
        switchMap((user) => {
          if (!user) {
            return EMPTY;
          }
          return forkJoin([
            projectService.getProjects(),
            projectService.getRecentProjects(user.id),
          ]);
        })
      )
      .subscribe(([projects, recentProjects]) => {
        if (projects) {
          this.allProjects = projects;
          this.recentProjects = recentProjects;
          if (this.timer) {
            // TODO can we combine these async streams?
            // this is because i have 2 async pathways.. projects and timer and i dont know
            this.timerUpdates(this.timer);
          }
        }
      });
  }

  ngOnInit() {}
  ngOnDestroy() {
    this.destroy.next();
  }
  createTimer() {
    this.viewingTimer = true;
    this.timerService.startTimer();
  }
  toggleTimerDetails() {
    this.viewingTimer = !this.viewingTimer;
  }
  closeTimerDisplay() {
    this.viewingTimer = false;
  }
  pauseTimer(event: Event) {
    this.timerService.pauseTimer();
    this.closeTimerDisplay();
    event.stopPropagation();
    return false;
  }
  resumeTimer(event: Event) {
    this.timerService.resumeTimer();
    this.closeTimerDisplay();
    event.stopPropagation();
    return false;
  }
  stopTimer(event: Event) {
    this.closeTimerDisplay();
    this.modalService.openModal("modal/time/add", {
      userid: this.user.id,
      fromTimer: true,
    });
    event.stopPropagation();
    return false;
  }
  deleteTimer() {
    // TODO confirmation
    this.closeTimerDisplay();
    this.timerService.stopTimer();
  }
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.projectSearch.isPopupOpen())
    );
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      // when there is no text it will show the top # of recent projects
      map((term) =>
        (term === ""
          ? this.recentProjects.slice(0, 10)
          : this.allProjects.filter(
              (p) => p.name.toLowerCase().indexOf(term.toLowerCase()) > -1
            )
        ).slice(0, 10)
      )
    );
  };
  projectSearchFormatter(project: IProject) {
    return project.name;
  }
  projectSelected(event: NgbTypeaheadSelectItemEvent) {
    if (event && event.item) {
      this.timerService.updateTimerProject(event.item.id);
    }
  }
  private timerUpdates = (timer: Timer) => {
    this.timer = timer;
    if (!this.timer) {
      // something is wrong then because we should always have a timer
      return;
    }
    this.timer.project = this.allProjects.find(
      (p) => p.id === this.timer.projectId
    );
    if (this.timer.project) {
      this.selectedProject = timer.project;
    } else {
      // the project might be gone or something now?
      // or if we filter for only active projects we dont want this timer to still be able to file time for it so unset the project fields?
      this.timer.project = null;
      this.selectedProject = null;
    }
  };
}
